import { Vector2 } from "../../../../../common/vector2.js";
import { CumulativeBoosterID } from "../../../../../cumulative_boosters/cumulative_booster_id.js";
import { Game } from "../../../../../game.js";
import { ImgID } from "../../../../../image_loader/img_id.js";
import { Easing } from "../../../../../transforms/transform_base.js";
import { TransformScale } from "../../../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../../../transforms/transform_straight_move.js";
import { IHighlightRect } from "../../../../common_ui_elements/abstract_tutorial_highlight.js";
import { UIElement } from "../../../../common_ui_elements/ui_element.js";
import { UIImage } from "../../../../common_ui_elements/ui_image.js";
import { GameScreen } from "../../game_screen.js";
import { ShotsTutorialHighlight } from "./shots_tutorial_highlight.js";
import { TableTutorialHighlight } from "./table_tutorial_highlight.js";
import { IController } from "../../../../../webgl_2d/core/types/controller_types.js";
import { Vfx2DManager } from "../../../../../webgl_2d/vfx_2d_manager.js";

enum TutorialStep
{
  None,
  TableHighlight,
  WaitingForSecondHint,
  ShotsCounterHighlight,
  BoosterSelection,
  BoosterAim,
}

export class GameTutorial extends UIElement
{
  private static readonly CURSOR_PULSE_SCALE: number = 0.8;
  private static readonly CURSOR_PULSE_DURATION_MS: number = 650;
  private static readonly SHOTS_HIGHLIGHT_MIN_VISIBLE_MS: number = 500;
  private static readonly SECOND_HINT_DELAY_MS: number = 100;

  private readonly game_screen: GameScreen;
  private readonly shots_bg: UIImage;
  private readonly move_shots_bg_to_front: () => void;

  private table_highlight!: TableTutorialHighlight;
  private shots_highlight!: ShotsTutorialHighlight;
  private cursor!: UIImage;
  private cursor_rest_pos!: Vector2;

  private checked: boolean = false;
  private step: TutorialStep = TutorialStep.None;
  private shots_at_start: number = 0;
  private shots_highlight_shown_at: number = 0;
  private first_shot_fired_at: number = 0;
  private suppressed = false;
  private booster_rect: IHighlightRect | null = null;
  private booster_id: CumulativeBoosterID | null = null;
  private booster_glow: IController | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    game_screen: GameScreen,
    shots_bg: UIImage,
    move_shots_bg_to_front: () => void
  )
  {
    super( ctx, 0, 0, width, height, false, false );

    this.game_screen = game_screen;
    this.shots_bg = shots_bg;
    this.move_shots_bg_to_front = move_shots_bg_to_front;

    // Purely a visual overlay: its full-screen rect must not swallow taps meant for
    // siblings underneath (e.g. the settings gear) in the UIElement touch-tree.
    this.setClickable( false );

    this.createHighlights();
    this.createCursor();
  }

  private createHighlights(): void
  {
    this.table_highlight = new TableTutorialHighlight( this.ctx, this.rect.getW(), this.rect.getH() );
    this.addChild( this.table_highlight );

    this.shots_highlight = new ShotsTutorialHighlight( this.ctx, this.rect.getW(), this.rect.getH() );
    this.addChild( this.shots_highlight );
  }

  private createCursor(): void
  {
    const w = LayoutScaler.widthFromDesign( 150 );
    const h = w * ( 183 / 159 );

    this.cursor = new UIImage( this.ctx, 0, 0, w, h, ImgID.IconTutorialCursor );
    this.cursor.setClickable( false );
    this.cursor.setOpacityImmediate( 0 );

    this.addChild( this.cursor );
  }

  private startCursorPulse(): void
  {
    const now = performance.now();
    const rect = this.cursor.getRect();
    const shrink = 1 - GameTutorial.CURSOR_PULSE_SCALE;
    const pressed_pos = new Vector2(
      this.cursor_rest_pos.x - shrink * rect.getW2(),
      this.cursor_rest_pos.y - shrink * rect.getH2()
    );
    const pulse_params = {
      easing: Easing.EaseInOutQuad,
      ping_pong_behaviour: true,
      infinite_loop: true,
    };

    this.cursor.addTransform( new TransformScale(
      this.cursor,
      true,
      null,
      new Vector2( GameTutorial.CURSOR_PULSE_SCALE, GameTutorial.CURSOR_PULSE_SCALE ),
      now,
      GameTutorial.CURSOR_PULSE_DURATION_MS,
      pulse_params
    ) );

    this.cursor.addTransform( new TransformStraightMove(
      this.cursor,
      true,
      this.cursor_rest_pos.clone(),
      pressed_pos,
      now,
      GameTutorial.CURSOR_PULSE_DURATION_MS,
      pulse_params
    ) );
  }

  private stopCursor(): void
  {
    this.cursor.stopAllTransforms();
    this.cursor.setScale( new Vector2( 1, 1 ) );
    if ( this.cursor_rest_pos )
      this.cursor.setPixelPos( this.cursor_rest_pos, true );
    this.cursor.hideElement();
    this.stopBoosterGlow();
  }

  private stopBoosterGlow(): void
  {
    if ( this.booster_glow )
    {
      this.booster_glow.stop();
      this.booster_glow = null;
    }
  }

  public startBoosterTutorial(
    booster_rect: { x: number, y: number, width: number, height: number },
    booster_id: CumulativeBoosterID
  ): void
  {
    this.checked = true;
    this.booster_id = booster_id;
    this.booster_rect = this.toContentLocalRect( booster_rect );
    this.showBoosterSelectionHint();
  }

  private showBoosterSelectionHint(): void
  {
    if ( !this.booster_rect )
      return;

    this.stopCursor();
    this.step = TutorialStep.BoosterSelection;
    this.cursor_rest_pos = new Vector2(
      this.booster_rect.x + this.booster_rect.width / 2,
      this.booster_rect.y + this.booster_rect.height * 0.28
    );
    this.cursor.setPixelPos( this.cursor_rest_pos, true );
    this.cursor.showElement();
    this.startCursorPulse();

    const center = new Vector2(
      Game.safeAreaLeft() + this.booster_rect.x + this.booster_rect.width / 2,
      Game.safeAreaTop() + this.booster_rect.y + this.booster_rect.height / 2
    );
    const booster_size = new Vector2( this.booster_rect.width, this.booster_rect.height );

    this.booster_glow = Vfx2DManager.addButtonBoosterGlow( center, booster_size );
  }

  public isWaitingForBoosterSelection(): boolean
  {
    return this.step === TutorialStep.BoosterSelection;
  }

  public isExpectedBooster( booster_id: CumulativeBoosterID ): boolean
  {
    return this.step === TutorialStep.BoosterSelection
      && this.booster_id === booster_id;
  }

  public onBoosterActivated( booster_id: CumulativeBoosterID ): boolean
  {
    if ( !this.isExpectedBooster( booster_id ) )
      return false;

    this.stopCursor();
    this.step = TutorialStep.BoosterAim;
    return true;
  }

  public onBoosterAimClosed(): void
  {
    if ( this.step === TutorialStep.BoosterAim )
      this.showBoosterSelectionHint();
  }

  public completeBoosterTutorial(): void
  {
    if ( this.step !== TutorialStep.BoosterAim )
      return;

    this.stopCursor();
    this.step = TutorialStep.None;
    if ( this.booster_id !== null )
      this.game_screen.completeBoosterTutorial( this.booster_id );
    this.booster_id = null;
  }

  private clientPointToLocalUI( client_x: number, client_y: number ): Vector2
  {
    const canvas_rect = this.ctx.canvas.getBoundingClientRect();
    const canvas_x = ( client_x - canvas_rect.left ) / canvas_rect.width * Game.screenW();
    const canvas_y = ( client_y - canvas_rect.top ) / canvas_rect.height * Game.screenH();

    return new Vector2(
      canvas_x - Game.safeAreaLeft(),
      canvas_y - Game.safeAreaTop()
    );
  }

  private toContentLocalRect( rect: { x: number, y: number, width: number, height: number } ): IHighlightRect
  {
    return {
      x: rect.x - Game.safeAreaLeft(),
      y: rect.y - Game.safeAreaTop(),
      width: rect.width,
      height: rect.height,
    };
  }

  private startStep1(): void
  {
    const table_rect = this.game_screen.getTableScreenRect();
    if ( !table_rect )
      return;

    this.checked = true;
    this.step = TutorialStep.TableHighlight;
    this.shots_at_start = this.game_screen.getShotsLeft();

    const padding = LayoutScaler.widthFromDesign( 20 );
    const top_left = this.clientPointToLocalUI( table_rect.x, table_rect.y );
    const bottom_right = this.clientPointToLocalUI( table_rect.x + table_rect.width, table_rect.y + table_rect.height );

    this.table_highlight.showRect( {
      x: top_left.x - padding,
      y: top_left.y - padding,
      width: bottom_right.x - top_left.x + padding * 2,
      height: bottom_right.y - top_left.y + padding * 2,
    } );

    const cutout_center = this.table_highlight.getCutoutCenter();
    if ( cutout_center )
    {
      this.cursor_rest_pos = cutout_center;
      this.cursor.setPixelPos( cutout_center, true );
      this.cursor.showElement();
      this.startCursorPulse();
    }
  }

  public onShotFired(): void
  {
    if ( this.step !== TutorialStep.TableHighlight )
      return;

    if ( this.game_screen.getShotsLeft() !== this.shots_at_start - 1 )
      return;

    this.cursor.stopAllTransforms();
    this.cursor.setScale( new Vector2( 1, 1 ) );
    this.cursor.setPixelPos( this.cursor_rest_pos, true );
    this.cursor.hideElement();

    this.step = TutorialStep.WaitingForSecondHint;
    this.first_shot_fired_at = performance.now();
    this.game_screen.setTutorialInputBlocked( true );
    this.table_highlight.hide();
  }

  private checkSecondHintDelay(): void
  {
    if ( this.game_screen.isFinished() )
    {
      this.step = TutorialStep.None;
      this.game_screen.setTutorialInputBlocked( false );
      return;
    }

    if ( performance.now() - this.first_shot_fired_at < GameTutorial.SECOND_HINT_DELAY_MS )
      return;

    this.step = TutorialStep.ShotsCounterHighlight;
    this.game_screen.setTutorialInputBlocked( false );

    this.move_shots_bg_to_front();

    const padding = LayoutScaler.widthFromDesign( 20 );
    const local = this.toContentLocalRect( this.shots_bg.getAbsoluteRect() );

    this.shots_highlight_shown_at = performance.now();
    this.shots_highlight.showRect( {
      x: local.x - padding,
      y: local.y - padding,
      width: local.width + padding * 2,
      height: local.height + padding * 2,
    } );
  }

  public onAimStart(): boolean
  {
    if ( this.suppressed )
      return false;

    if ( this.step !== TutorialStep.ShotsCounterHighlight )
      return false;

    if ( performance.now() - this.shots_highlight_shown_at < GameTutorial.SHOTS_HIGHLIGHT_MIN_VISIBLE_MS )
      return true;

    this.step = TutorialStep.None;
    this.shots_highlight.hide();
    this.game_screen.completeTutorial();

    return true;
  }

  public isActive(): boolean
  {
    return !this.checked || this.step !== TutorialStep.None;
  }

  public isTapAllowed( client_x: number, client_y: number ): boolean
  {
    if ( this.suppressed )
      return true;

    if ( this.step === TutorialStep.BoosterSelection )
    {
      if ( !this.booster_rect )
        return false;

      const point = this.clientPointToLocalUI( client_x, client_y );
      return point.x >= this.booster_rect.x
        && point.x <= this.booster_rect.x + this.booster_rect.width
        && point.y >= this.booster_rect.y
        && point.y <= this.booster_rect.y + this.booster_rect.height;
    }

    if ( this.step !== TutorialStep.TableHighlight )
      return true;

    const point = this.clientPointToLocalUI( client_x, client_y );
    return this.table_highlight.isPointInCutout( point );
  }

  public update(): void
  {
    if ( this.suppressed )
      return;

    if ( !this.checked )
    {
      if ( this.game_screen.isEligibleForTutorial() )
        this.startStep1();
      else
        this.checked = true;
    }

    if ( this.step === TutorialStep.WaitingForSecondHint )
      this.checkSecondHintDelay();
  }

  public setSuppressed( suppressed: boolean ): void
  {
    if ( this.suppressed === suppressed )
      return;

    this.suppressed = suppressed;
    this.game_screen.setTutorialInputBlocked(
      !suppressed && this.step === TutorialStep.WaitingForSecondHint
    );

    if ( suppressed )
      this.hideElement();
    else
      this.showElement();
  }

  public name(): string
  {
    return "GameTutorial";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
